/**
 * Leichtgewichtiger Linechart als inline-SVG.
 * Erwartet eine Liste von Serien: [{ name, color, points: [{date, value}] }]
 * Alle Serien werden gegen die gleiche x-Achse (Datum) gerendert.
 */
export default function LineChart({ series, height = 200, format = (v) => v }) {
  const allDates = new Set();
  series.forEach((s) => s.points.forEach((p) => allDates.add(p.date)));
  const dates = Array.from(allDates).sort();

  if (dates.length < 2) {
    return (
      <div className="flex items-center justify-center text-[12px] text-gray-400" style={{ height }}>
        Noch nicht genug Daten für diesen Zeitraum
      </div>
    );
  }

  // Jede Serie auf gleiche x-Länge normalisieren (fehlende Tage = 0)
  const normalized = series.map((s) => {
    const lookup = Object.fromEntries(s.points.map((p) => [p.date, Number(p.value) || 0]));
    return {
      ...s,
      values: dates.map((d) => lookup[d] ?? 0),
    };
  });

  const allValues = normalized.flatMap((s) => s.values);
  const min = 0;
  const max = Math.max(...allValues, 1);
  const range = max - min;

  const width = 720;
  const padding = { top: 10, right: 12, bottom: 22, left: 36 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const step = dates.length > 1 ? innerW / (dates.length - 1) : innerW;

  const pathFor = (values) =>
    values
      .map((v, i) => {
        const x = padding.left + i * step;
        const y = padding.top + innerH - ((v - min) / range) * innerH;
        return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(' ');

  // y-Achsen-Ticks (5 Schritte)
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => ({
    v: Math.round(min + range * t),
    y: padding.top + innerH - t * innerH,
  }));

  // x-Achse: alle ~6 Datumseinträge beschriften
  const xTickEvery = Math.max(1, Math.floor(dates.length / 6));

  return (
    <div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height }}>
        {/* Grid */}
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={padding.left} x2={width - padding.right} y1={t.y} y2={t.y} stroke="#E5E7EB" strokeWidth="1" />
            <text x={padding.left - 6} y={t.y + 4} textAnchor="end" fontSize="10" fill="#9CA3AF">
              {format(t.v)}
            </text>
          </g>
        ))}
        {/* Datum-Ticks */}
        {dates.map((d, i) => {
          if (i % xTickEvery !== 0 && i !== dates.length - 1) return null;
          const x = padding.left + i * step;
          const label = new Date(d).toLocaleDateString('de', { day: '2-digit', month: '2-digit' });
          return (
            <text key={d} x={x} y={height - 6} textAnchor="middle" fontSize="10" fill="#9CA3AF">
              {label}
            </text>
          );
        })}
        {/* Linien */}
        {normalized.map((s) => (
          <path
            key={s.name}
            d={pathFor(s.values)}
            fill="none"
            stroke={s.color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
      </svg>
      {/* Legende */}
      <div className="flex flex-wrap gap-4 mt-2 px-2">
        {normalized.map((s) => (
          <div key={s.name} className="flex items-center gap-1.5 text-[11px] text-gray-600">
            <span className="w-3 h-[2px]" style={{ backgroundColor: s.color }} />
            {s.name}
          </div>
        ))}
      </div>
    </div>
  );
}
